import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { Post } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLangFade } from '../hooks/useLangFade';

const AllGalleryPage: React.FC = () => {
  useDocumentTitle('Галерија — Манастир Градац', '/galerija');
  const { t } = useScript();
  const langFade = useLangFade();
  const { isAdmin, token } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxPost, setLightboxPost] = useState<Post | null>(null);

  const loadPosts = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getPosts()
      .then(items => { setPosts(items); setLoading(false); })
      .catch((e: ApiError) => {
        setError(e?.message || 'Грешка при учитавању.');
        setLoading(false);
      });
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const handleDelete = async (id: number) => {
    if (!token || !window.confirm(t('Обрисати ову ставку?'))) return;
    try {
      await api.deletePost(token, id);
      setPosts(prev => prev.filter(p => p.id !== id));
    } catch {}
  };

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none';
  };

  const movePost = async (index: number, direction: 'up' | 'down') => {
    if (!token) return;
    const other = direction === 'up' ? index - 1 : index + 1;
    const updated = [...posts];
    const tempOrder = updated[index].sortOrder;
    updated[index] = { ...updated[index], sortOrder: updated[other].sortOrder };
    updated[other] = { ...updated[other], sortOrder: tempOrder };
    [updated[index], updated[other]] = [updated[other], updated[index]];
    setPosts(updated);
    try {
      await api.reorderPosts(token, [
        { id: updated[index].id, sortOrder: updated[index].sortOrder },
        { id: updated[other].id, sortOrder: updated[other].sortOrder },
      ]);
    } catch {
      // revert on failure
      loadPosts();
    }
  };

  // Close lightbox on Escape key
  useEffect(() => {
    if (!lightboxPost) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxPost(null);
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [lightboxPost]);

  // Navigate lightbox with arrows
  const navigateLightbox = useCallback((dir: 'prev' | 'next') => {
    if (!lightboxPost) return;
    const idx = posts.findIndex(p => p.id === lightboxPost.id);
    if (idx === -1) return;
    const newIdx = dir === 'prev' ? idx - 1 : idx + 1;
    if (newIdx >= 0 && newIdx < posts.length) {
      setLightboxPost(posts[newIdx]);
    }
  }, [lightboxPost, posts]);

  useEffect(() => {
    if (!lightboxPost) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
      if (e.key === 'ArrowRight') navigateLightbox('next');
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [lightboxPost, navigateLightbox]);

  const currentIdx = lightboxPost ? posts.findIndex(p => p.id === lightboxPost.id) : -1;

  return (
    <div className="gallery-page">
      <div className="gallery-page-header">
        <h1 className="news-heading">{t('ГАЛЕРИЈА МАНАСТИРА ГРАДАЦ')}</h1>
        {isAdmin && (
          <div className="news-admin-toolbar">
            <button className="btn-primary" onClick={() => navigate('/galerija/nova')}>
              {t('+ Додај')}
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="state-block" role="status" aria-live="polite">
          <div className="state-spinner" aria-hidden="true" />
          <span>{t('Учитавање...')}</span>
        </div>
      )}

      {error && !loading && (
        <div className="state-block state-error" role="alert">
          <span>{t(error)}</span>
          <button type="button" className="state-retry" onClick={loadPosts}>
            {t('Покушај поново')}
          </button>
        </div>
      )}

      {!loading && !error && (
      <div className={`gpage-grid ${langFade}`}>
        {posts.map((post, index) => (
          <div
            key={post.id}
            className="gpage-item"
            onClick={() => { if (!isAdmin) setLightboxPost(post); }}
          >
            <div className="gpage-img-wrap">
              <img
                src={post.imageUrl}
                alt={t(post.title)}
                className="gpage-img"
                onError={handleImgError}
                width={800}
                height={600}
                decoding="async"
                loading={index < 4 ? 'eager' : 'lazy'}
              />
              <div className="gpage-img-overlay" />
              <div className="gpage-img-info">
                <span className="gpage-img-icon">✦</span>
                <span className="gpage-img-title">{t(post.title)}</span>
              </div>
            </div>

            {isAdmin && (
              <div className="gpage-admin-bar" onClick={e => e.stopPropagation()}>
                <button
                  className="gpage-admin-btn"
                  disabled={index === 0}
                  onClick={() => movePost(index, 'up')}
                  aria-label={t('Помери горе')}
                >↑</button>
                <button
                  className="gpage-admin-btn"
                  disabled={index === posts.length - 1}
                  onClick={() => movePost(index, 'down')}
                  aria-label={t('Помери доле')}
                >↓</button>
                <button
                  className="gpage-admin-btn"
                  onClick={() => navigate(`/galerija/${post.id}/uredi`)}
                >{t('Уреди')} ✎</button>
                <button
                  className="gpage-admin-btn gpage-admin-delete"
                  onClick={() => handleDelete(post.id)}
                >{t('Обриши')} ✕</button>
                {post.featured && (
                  <span className="gpage-admin-featured">★ {t('На почетној')}</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {/* Lightbox Modal */}
      {lightboxPost && (
        <div className="glightbox-backdrop" onClick={() => setLightboxPost(null)}>
          <div className="glightbox-container" onClick={e => e.stopPropagation()}>
            <button
              className="glightbox-close"
              onClick={() => setLightboxPost(null)}
              aria-label={t('Затвори')}
            >×</button>

            {/* Navigation arrows */}
            {currentIdx > 0 && (
              <button
                className="glightbox-nav glightbox-nav-prev"
                onClick={() => navigateLightbox('prev')}
                aria-label={t('Претходна')}
              >‹</button>
            )}
            {currentIdx < posts.length - 1 && (
              <button
                className="glightbox-nav glightbox-nav-next"
                onClick={() => navigateLightbox('next')}
                aria-label={t('Следећа')}
              >›</button>
            )}

            <div className="glightbox-image-wrapper">
              <img
                src={lightboxPost.imageUrl}
                alt={lightboxPost.title}
                className="glightbox-image"
              />
            </div>
            <div className="glightbox-info">
              <h3 className="glightbox-title">{t(lightboxPost.title)}</h3>
              <p className="glightbox-description">{t(lightboxPost.description)}</p>
              <span className="glightbox-counter">
                {currentIdx + 1} / {posts.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllGalleryPage;
